import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, ChevronRight, Maximize2, Minimize2, X, ZoomIn, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface MatchData {
  id: number;
  matchNumber: number;
  player1Id: number;
  player2Id: number;
  winnerId: number | null;
  round: number;
  status: string;
  gameRoomId?: number;
}

interface Player {
  id: number;
  username: string;
}

interface InteractiveTournamentBracketProps {
  matches: MatchData[];
  players: Player[];
  maxRounds: number;
  userId?: number;
  winnerUserId?: number | null;
  isCreator?: boolean;
  onEditWinner?: (matchId: number, player1Id: number, player2Id: number) => void;
  onMatchClick?: (match: MatchData) => void;
}

export function InteractiveTournamentBracket({ 
  matches, 
  players, 
  maxRounds, 
  userId, 
  winnerUserId,
  isCreator = false,
  onEditWinner,
  onMatchClick
}: InteractiveTournamentBracketProps) {
  // State for interactive features
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [highlightedMatch, setHighlightedMatch] = useState<number | null>(null);
  const [showConnections, setShowConnections] = useState(true);
  const [animationComplete, setAnimationComplete] = useState(false);
  const bracketRef = useRef<HTMLDivElement>(null);

  // Organize matches by round
  const matchesByRound = matches.reduce((acc, match) => {
    if (!acc[match.round]) {
      acc[match.round] = [];
    }
    acc[match.round].push(match);
    return acc;
  }, {} as Record<number, MatchData[]>);

  // Helper function to get player name
  const getPlayerName = (playerId: number) => {
    if (playerId === userId) return "You";
    const player = players.find(p => p.id === playerId);
    return player ? player.username : `Player #${playerId}`;
  };

  // Calculate match connections between rounds
  const getMatchConnections = (currentRound: number) => {
    if (currentRound >= maxRounds) return [];
    
    const currentMatches = matchesByRound[currentRound] || [];
    const nextRoundMatches = matchesByRound[currentRound + 1] || [];
    
    return currentMatches.map((match) => {
      if (!match.winnerId) return null;
      
      // Find the next round match this player advances to
      const nextMatch = nextRoundMatches.find(
        (nextMatch) => 
          nextMatch.player1Id === match.winnerId || 
          nextMatch.player2Id === match.winnerId
      );
      
      return nextMatch ? { 
        fromMatch: match.id, 
        toMatch: nextMatch.id,
        winnerId: match.winnerId 
      } : null;
    }).filter(Boolean);
  };

  // Calculate spacing between matches based on round
  const getMatchSpacing = (round: number) => {
    const baseSpacing = 6; // Base spacing in rem
    return round === 1 ? baseSpacing : baseSpacing * Math.pow(1.5, round - 1);
  };

  // Toggle fullscreen mode
  const toggleFullscreen = () => {
    if (isFullscreen) {
      document.exitFullscreen().catch(err => console.error('Error exiting fullscreen:', err));
      setIsFullscreen(false);
    } else {
      if (bracketRef.current) {
        bracketRef.current.requestFullscreen().catch(err => console.error('Error entering fullscreen:', err));
        setIsFullscreen(true);
      }
    }
  };
  
  // Listen for fullscreen change events from outside this component
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Handle match click
  const handleMatchClick = (match: MatchData) => {
    setHighlightedMatch(match.id === highlightedMatch ? null : match.id);
    if (onMatchClick) {
      onMatchClick(match);
    }
  };

  // Zoom controls
  const zoomIn = () => setZoom(prev => Math.min(prev + 0.2, 2));
  const zoomOut = () => setZoom(prev => Math.max(prev - 0.2, 0.5));
  const resetZoom = () => setZoom(1);

  // Animation effect when component mounts
  useEffect(() => {
    // Start animation after a slight delay
    const timer = setTimeout(() => {
      setAnimationComplete(true);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div 
      ref={bracketRef} 
      className={`relative overflow-hidden rounded-xl transition-all ${isFullscreen ? 'fixed inset-0 z-50 bg-black' : 'my-6 max-w-full'}`}
    >
      {/* Controls */}
      <div className="absolute top-2 right-2 flex gap-2 z-10">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" onClick={() => setShowConnections(!showConnections)} className="h-8 w-8 bg-black/40 text-white hover:bg-black/50 rounded-full">
                <Play className="h-4 w-4" style={{ transform: 'rotate(90deg)' }} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{showConnections ? "Hide" : "Show"} connections</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" onClick={zoomIn} className="h-8 w-8 bg-black/40 text-white hover:bg-black/50 rounded-full">
                <ZoomIn className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Zoom in</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" onClick={zoomOut} className="h-8 w-8 bg-black/40 text-white hover:bg-black/50 rounded-full">
                <ZoomIn className="h-4 w-4" style={{ transform: 'scale(0.8)' }} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Zoom out</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        {zoom !== 1 && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" onClick={resetZoom} className="h-8 w-8 bg-black/40 text-white hover:bg-black/50 rounded-full">
                  <X className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Reset zoom</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" onClick={toggleFullscreen} className="h-8 w-8 bg-black/40 text-white hover:bg-black/50 rounded-full">
                {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Bracket visualization */}
      <div 
        className={`pb-4 ${isFullscreen ? 'h-full pt-12 flex items-center justify-center bg-black/90' : ''}`}
        style={{ overflowX: 'auto', overflowY: 'auto' }}
      >
        <div 
          className="flex flex-row-reverse justify-center items-start gap-3 lg:gap-5 min-w-[600px]"
          style={{ 
            transform: `scale(${zoom})`,
            transformOrigin: 'center center',
            transition: 'transform 0.3s ease'
          }}
        >
          {/* We reverse the order of rounds to make final appear on the right side */}
          {Array.from({ length: maxRounds }, (_, i) => maxRounds - i).map((round) => {
            const roundMatches = matchesByRound[round] || [];
            const matchConnections = getMatchConnections(round);
            const spacing = getMatchSpacing(round);
            
            return (
              <motion.div 
                key={round} 
                className="flex flex-col relative"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ 
                  duration: 0.5,
                  delay: (maxRounds - round) * 0.15
                }}
              >
                <div className="text-center font-medium mb-3 px-2 py-1 bg-primary/10 rounded-md text-sm">
                  {round === 1 ? "First Round" : 
                   round === maxRounds ? "Final" : 
                   `Round ${round}`}
                  <span className="ml-1 text-xs opacity-60">({roundMatches.length})</span>
                </div>

                <div 
                  className="flex flex-col" 
                  style={{ 
                    gap: `${spacing}rem`,
                    marginTop: round > 1 ? `${(round - 1) * 2}rem` : "0" 
                  }}
                >
                  {roundMatches.map((match, matchIndex) => {
                    // Special case for bye matches where both players are the same
                    const isByeMatch = match.player1Id === match.player2Id;
                    const isHighlighted = match.id === highlightedMatch;
                    
                    return (
                      <motion.div 
                        key={match.id} 
                        className="relative bracket-match-container" 
                        data-match-id={match.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ 
                          duration: 0.3,
                          delay: 0.3 + (matchIndex * 0.1) + (maxRounds - round) * 0.15
                        }}
                      >
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.98 }}
                          animate={isHighlighted ? { scale: 1.05, boxShadow: '0 0 0 2px rgba(var(--primary), 0.5)' } : { scale: 1 }}
                          onClick={() => handleMatchClick(match)}
                        >
                          <Card className={`theme-card overflow-hidden w-36 cursor-pointer transition-all
                            ${match.status === 'completed' ? 'border-primary/20' : ''}
                            ${isHighlighted ? 'ring-2 ring-primary' : ''}
                          `}>
                            <CardContent className="p-3">
                              <div className="text-xs font-medium mb-1 flex justify-between items-center">
                                <span>Match #{match.matchNumber}</span>
                                {match.status === 'completed' ? (
                                  <Badge variant="default" className="text-[10px] px-1 py-0 h-4 text-white">
                                    ✓
                                  </Badge>
                                ) : match.status === 'active' ? (
                                  <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 border-accent text-white">
                                    Live
                                  </Badge>
                                ) : null}
                              </div>
                              
                              {isByeMatch ? (
                                <div className="flex items-center gap-1 py-2">
                                  <Badge variant="outline" className="text-[10px] px-1 py-0 text-white">Bye</Badge>
                                  <div className="text-xs font-bold">{getPlayerName(match.player1Id)}</div>
                                </div>
                              ) : (
                                <div className="flex flex-col gap-2 py-1">
                                  <div className={`text-sm px-1 py-0.5 rounded ${match.winnerId === match.player1Id ? "font-bold text-primary bg-primary/10" : ""}`}>
                                    {getPlayerName(match.player1Id)}
                                  </div>
                                  
                                  <div className="text-xs theme-text-muted text-center">vs</div>
                                  
                                  <div className={`text-sm px-1 py-0.5 rounded ${match.winnerId === match.player2Id ? "font-bold text-primary bg-primary/10" : ""}`}>
                                    {getPlayerName(match.player2Id)}
                                  </div>
                                  
                                  {/* Edit winner button for tournament creator */}
                                  {isCreator && onEditWinner && match.status !== 'pending' && (
                                    <button 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onEditWinner(match.id, match.player1Id, match.player2Id);
                                      }}
                                      className="mt-1 text-[10px] px-1 py-0.5 rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors text-center"
                                    >
                                      Edit Winner
                                    </button>
                                  )}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        </motion.div>
                        
                        {/* Show advancing arrow for winners */}
                        {match.winnerId && round < maxRounds && showConnections && (
                          <motion.div 
                            className="absolute right-0 top-1/2 w-6 flex items-center justify-center" 
                            style={{ transform: "translateY(-50%)" }}
                            initial={{ opacity: 0, x: -5 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.6 + (matchIndex * 0.1) }}
                          >
                            <ChevronRight className="h-4 w-4 text-primary" />
                          </motion.div>
                        )}
                        
                        {/* Connection lines to next round */}
                        {match.winnerId && round < maxRounds && showConnections && animationComplete && (
                          <motion.div 
                            className="absolute right-0 top-1/2 w-4 border-t border-primary" 
                            style={{ transform: "translateY(-50%)" }}
                            initial={{ width: 0 }}
                            animate={{ width: "1rem" }}
                            transition={{ duration: 0.3, delay: 0.6 + (matchIndex * 0.1) }}
                          />
                        )}
                        
                        {/* Connection lines from previous round */}
                        {round < maxRounds && match.winnerId && showConnections && animationComplete && (
                          <motion.div
                            className="absolute left-0 top-1/2 w-4 border-t border-primary" 
                            style={{ transform: "translateY(-50%)" }}
                            initial={{ width: 0 }}
                            animate={{ width: "1rem" }}
                            transition={{ duration: 0.3, delay: 0.6 + (matchIndex * 0.1) }}
                          />
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}

          {/* Tournament winner trophy - now positioned at the beginning of the flex container */}
          {winnerUserId && (
            <motion.div 
              className="flex flex-col items-center justify-center mt-16 order-first"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
            >
              <motion.div 
                className="h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center"
                animate={{ 
                  backgroundColor: ["rgba(var(--primary), 0.1)", "rgba(var(--primary), 0.3)", "rgba(var(--primary), 0.1)"],
                  scale: [1, 1.05, 1]
                }}
                transition={{ 
                  duration: 3,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              >
                <Trophy className="h-8 w-8 text-primary" />
              </motion.div>
              <div className="text-md font-bold mt-3 text-primary">
                {winnerUserId === userId ? "You Won!" : getPlayerName(winnerUserId)}
              </div>
              <div className="text-sm opacity-70">Champion</div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}